import React, { useEffect, useState } from "react"
import { Box, Dialog, DialogContent, styled, Typography, IconButton } from "@mui/material"
import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import { Formik } from "formik"
import {
  validationSchemaDynamicField,
  validationSchemaDynamicGroup
} from "helpers/validationSchema"
import CloseIcon from "@mui/icons-material/Close"
import { useTheme } from "@mui/system"
import { useDispatch, useSelector } from "react-redux"
import { addDynamicFieldsAction, getHotelCategoryDetailAction } from "redux/hotel/action"
import AppSelect from "components/StyledComponents/AppSelect"

const buttonText = {
  CANCEL: "Cancel",
  SAVE: "Save",
  UPDATE: "Update"
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: "none",
    width: 580,
    padding: 35,
    boxSizing: "border-box"
  }
}))

function DynamicFieldModal({
  groupData = {},
  isGroup = false,
  open = false,
  onClose = () => {},
  isEdit = false,
  selectedCategoryId,
  fieldData,
  HotelCategory
}) {
  const theme = useTheme()
  const [btnValue, setBtnValue] = useState("")
  const { dynamicFieldsGroup } = useSelector((store) => store?.hotel)
  const dispatch = useDispatch()

  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: theme.palette.background.main
    }
  }
  const initialValues = () => {
    if (isEdit) {
      return {
        name: fieldData ? fieldData?.label : "",
        type: fieldData ? fieldData?.type : "",
        showListing: fieldData ? fieldData?.show_listing : false,
        originalName: fieldData ? fieldData?.label : ""
      }
    }
    return isGroup
      ? {
          name: "",
          type: ""
        }
      : {
          name: "",
          type: ""
        }
  }
  const handleButton = (value, btnValue) => {
    if (btnValue?.trim() === buttonText.CANCEL) {
      onClose()
    } else if (btnValue?.trim() === buttonText.SAVE) {
      if (isGroup) {
        const groupField = {
          key: value?.name?.trim().toLowerCase().replace(/\s+/g, "_") || "",
          label: value?.name || "",
          type: value?.type || "",
          show_listing: false,
          fields: []
        }

        const updatedGroups = [
          ...dynamicFieldsGroup.filter((group) => group?.key !== "common"),
          groupField
        ]

        dispatch(
          addDynamicFieldsAction({
            payload: { dynamic_fields: updatedGroups, action: "create" },
            onClose,
            id: selectedCategoryId
          })
        )
      } else {
        let updatedFieldsGroup = dynamicFieldsGroup?.map((grp) => {
          if (grp.key === groupData?.key) {
            return {
              ...grp,
              fields: [
                ...grp.fields,
                {
                  key: value?.name?.trim().toLowerCase().replace(/\s+/g, "_") || "",
                  label: value?.name || "",
                  type: value?.type || "",
                  show_listing: false
                }
              ]
            }
          }
          return grp
        })
        updatedFieldsGroup = updatedFieldsGroup?.filter((group) => group?.key !== "common")

        dispatch(
          addDynamicFieldsAction({
            payload: { dynamic_fields: updatedFieldsGroup, action: "create" },
            onClose,
            id: selectedCategoryId
          })
        )
      }
    }
  }

  const handleEdit = (value) => {
    if (isGroup) {
      let updatedGroups = dynamicFieldsGroup?.map((group) =>
        group.key === fieldData?.key
          ? {
              ...group,
              key: value?.name?.trim().toLowerCase().replace(/\s+/g, "_") || "",
              label: value?.name || "",
              type: value?.type || "",
              show_listing: fieldData?.show_listing || false
            }
          : group
      )
      updatedGroups = updatedGroups.filter((group) => group?.key !== "common")
      dispatch(
        addDynamicFieldsAction({
          payload: { dynamic_fields: updatedGroups, action: "edit" },
          onClose,
          id: selectedCategoryId
        })
      )
    } else {
      const newField = {
        key: value?.name?.trim().toLowerCase().replace(/\s+/g, "_") || "",
        label: value?.name || "",
        type: value?.type || "",
        show_listing: fieldData?.show_listing || false
      }

      let updatedGroups = dynamicFieldsGroup?.map((group) => {
        if (group.key === groupData?.key) {
          const updatedFields = group.fields.map((field) =>
            field.label?.toLowerCase() === value?.originalName?.toLowerCase() ? newField : field
          )

          return {
            ...group,
            fields: updatedFields
          }
        }
        return group
      })
      updatedGroups = updatedGroups?.filter((group) => group?.key !== "common")
      dispatch(
        addDynamicFieldsAction({
          payload: { dynamic_fields: updatedGroups, action: "edit" },
          onClose,
          id: selectedCategoryId
        })
      )
    }
  }

  const handleSubmit = (values) => {
    if (btnValue === buttonText.UPDATE) {
      handleEdit(values, btnValue)
    } else {
      handleButton(values, btnValue)
    }
  }

  useEffect(() => {
    if (isEdit) dispatch(getHotelCategoryDetailAction(selectedCategoryId))
  }, [open])

  return (
    <StyledDialog open={open}>
      <DialogContent>
        <Box className="d-flex justify-content-end">
          <IconButton onClick={() => onClose()} sx={styles.closeBtn}>
            <CloseIcon sx={{ height: "14px", width: "14px" }} />
          </IconButton>
        </Box>
        <Box className="w-100 d-flex justify-content-center mb-2">
          <Typography variant="h2">
            {isEdit ? `Edit ${isGroup ? "Group" : "Field"}` : `Add ${isGroup ? "Group" : "Field"}`}
          </Typography>
        </Box>
        <Formik
          initialValues={initialValues()}
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validationSchema={isGroup ? validationSchemaDynamicGroup : validationSchemaDynamicField}>
          {(formik) => (
            <>
              <Box flexDirection="column" className="mt-4">
                <React.Fragment>
                  <Box className="d-flex justify-content-between w-100 py-2">
                    <AppTextField
                      id="name"
                      name="name"
                      fullWidth
                      isUpperCase={!false}
                      label={`Name`}
                      isRequired={true}
                      placeholder="Enter Name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.errors.name}
                    />
                  </Box>
                </React.Fragment>
              </Box>

              <Box flexDirection="column" className="mt-1">
                <Box className="d-flex justify-content-between w-100 py-2">
                  <AppSelect
                    id="type"
                    name="type"
                    label=" Type"
                    isRequired={true}
                    menuItems={
                      isGroup
                        ? HotelCategory?.toLowerCase()?.includes("restaurant")
                          ? restaurantGroupTypeOptions
                          : groupTypeOptions
                        : typeOptions
                    }
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    error={formik.touched.type && formik.errors.type}
                  />
                </Box>
              </Box>

              <Box className="mt-5 d-flex justify-content-around">
                <AppButton
                  onClick={() => {
                    setBtnValue(isEdit ? buttonText.UPDATE : buttonText.SAVE)
                    formik.handleSubmit()
                  }}
                  sx={{ height: "48px", width: 150 }}
                  variant="outlined">
                  {isEdit ? buttonText.UPDATE : buttonText.SAVE}
                </AppButton>
                <AppButton
                  onClick={(e) => handleButton(formik, e.target.innerText)}
                  sx={{ height: "48px", width: 150 }}
                  variant="outlined">
                  {buttonText.CANCEL}
                </AppButton>
              </Box>
            </>
          )}
        </Formik>
      </DialogContent>
    </StyledDialog>
  )
}

export default DynamicFieldModal

const typeOptions = [
  {
    value: "text",
    label: "Text"
  },
  {
    value: "number",
    label: "Number"
  },
  {
    value: "image",
    label: "Image"
  },
  {
    value: "time",
    label: "Time"
  },
  {
    value: "file",
    label: "File"
  }
]

const groupTypeOptions = [
  {
    value: "section",
    label: "Section"
  },
  {
    value: "file_image_download",
    label: "File Download"
  }
]

const restaurantGroupTypeOptions = [
  {
    value: "section",
    label: "Section"
  },
  {
    value: "image_Scroll",
    label: "Image Scroll"
  },
  {
    value: "week_time_range",
    label: "Weekly Schedule"
  },
  {
    value: "card_with_background",
    label: "Card with Background"
  },
  {
    value: "file_image_download",
    label: "File Download"
  },
  {
    value: "daily_hours",
    label: "Daily hours"
  }
]
